import React, { ComponentType } from 'react';
import { withRouter, NextRouter } from 'next/router';
import { BaseContext } from 'next/dist/shared/lib/utils';

import Error from '../../_error';
import { Item, ProductCategoryFilter } from 'types';
import productsService, { CategoryDto } from 'data/products';
import DoubleHeader from 'components/DoubleHeader';
import AccessoriesCopy from 'components/collection/accessories';
import ShoesAndSocksCopy from 'components/collection/shoes-and-socks';
import ShirtsCopy from 'components/collection/shirts';
import TuxedosAndSuitsCopy from 'components/collection/tuxedos-and-suits';
import TiesCopy from 'components/collection/ties';
import VestsAndCummerbundCopy from 'components/collection/vests-and-cummerbund';
import Filter from 'partials/products/CollectionFilter';
import CollectionList from 'partials/products/CollectionList';
import { getProducts, getBundles } from 'services/productsApi';
import { capitalizeFirstLetter, getParameterByName, combinePredicatesUsingOr, constructQueryStringFromFilters } from 'utils/utils';
import { withPageStaticProps } from 'utils/header';
import { productListFiltered } from 'utils/metrics';
import { ColorFilter } from '@root/src/partials/visualizer';

const getCategoryFromSlug = (categorySlug: string) => productsService.categories.find((category) => category.slug === categorySlug);

const parseFiltersFromUrl = (filters: string) =>
  decodeURIComponent(filters)
    .split(',')
    .map((filter) => filter.replace(/\+/g, ' ').split(' ').map(capitalizeFirstLetter).join(' '));

const slugToAboutContent: { [key: string]: ComponentType } = {
  accessories: AccessoriesCopy,
  shirts: ShirtsCopy,
  'shoes-and-socks': ShoesAndSocksCopy,
  ties: TiesCopy,
  'tuxedos-and-suits': TuxedosAndSuitsCopy,
  'vests-and-cummerbunds': VestsAndCummerbundCopy,
};

const AboutContent = ({ category }: { category: string | null }) => {
  if (!category || !(category in slugToAboutContent)) {
    return null;
  }

  const CopyComponent = slugToAboutContent[category];

  return (
    <div className="bg-gray py-64 sm:py-128">
      <CopyComponent />
    </div>
  );
};

interface Props {
  activeCategory: CategoryDto;
  products: Item[];
  error?: number;
  router: NextRouter;
}

interface State {
  categoryFilters: ProductCategoryFilter[];
  colorFilters: string[];
  patternFilters: string[];
  procurementFilters: string[];
  tieTypeFilters: string[];
}

class List extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      categoryFilters: [],
      colorFilters: [],
      patternFilters: [],
      procurementFilters: [],
      tieTypeFilters: [],
    };
  }

  componentDidMount() {
    this.mapPropsToState();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.activeCategory.name !== prevProps.activeCategory.name) {
      this.mapPropsToState();
    }

    // Update URL filter param
    if (
      prevState.colorFilters !== this.state.colorFilters ||
      prevState.patternFilters !== this.state.patternFilters ||
      prevState.categoryFilters !== this.state.categoryFilters ||
      prevState.procurementFilters !== this.state.procurementFilters ||
      prevState.tieTypeFilters !== this.state.tieTypeFilters
    ) {
      // The logic for updating the route
      const filtersMap = {
        color: this.state.colorFilters,
        pattern: this.state.patternFilters,
        type: this.state.categoryFilters,
        procurement: this.state.procurementFilters,
        tieType: this.state.tieTypeFilters,
        // Add your new filter here. It can be an array or a single value.
      };

      const queryString = constructQueryStringFromFilters(filtersMap);
      let newRoute = window.location.pathname;

      if (queryString) {
        newRoute += `?${queryString}`;
      }

      this.props.router.replace(newRoute, undefined, { shallow: true, scroll: false });
    }
  }

  getFiltersMatchingActiveCategory(categories: CategoryDto[]): ProductCategoryFilter[] {
    const matchingCategory = categories.find((category) => category.displayName.includes(this.props.activeCategory.displayName));

    if (!matchingCategory) {
      return [];
    }

    return matchingCategory.categoryFilters;
  }

  mapPropsToState = () => {
    // Next step is to get the type from the url and add it to the category filters.
    const color = getParameterByName('color');
    const pattern = getParameterByName('pattern');
    const procurement = getParameterByName('procurement');
    const tieType = getParameterByName('tieType');
    const type = getParameterByName('type');
    // Need to get category filters for type, should look in products.js
    const allCategoryFilters = type ? this.getFiltersMatchingActiveCategory(productsService.categories) : [];
    const categoryFilters = allCategoryFilters.filter((filter) => parseFiltersFromUrl(type || '').includes(filter.displayName));
    const colorFilters: ColorFilter[] = color ? (parseFiltersFromUrl(color) as ColorFilter[]) : [];
    const patternFilters = pattern ? parseFiltersFromUrl(pattern) : [];
    const procurementFilters = procurement ? parseFiltersFromUrl(procurement) : [];
    const tieTypeFilters = tieType ? parseFiltersFromUrl(tieType) : [];

    this.setState(() => ({
      categoryFilters,
      colorFilters,
      patternFilters,
      procurementFilters,
      tieTypeFilters,
    }));
  };

  trackProductListFiltered = () =>
    productListFiltered(
      this.props.activeCategory.name,
      {
        categories: this.state.categoryFilters,
        colors: this.state.colorFilters,
        patterns: this.state.patternFilters,
        procurement: this.state.procurementFilters,
        tieTypes: this.state.tieTypeFilters,
      },
      this.getFilteredProducts(),
    );

  headerContent = (displayName: string) => (
    <>
      <div className="col-start-1 col-end-13 sm:col-start-4 md:col-start-3">
        <div className="mb-32 xs:mb-64">
          <DoubleHeader
            hero
            smallTitle={<h2>Collection</h2>}
            largeTitle={<h1>{displayName === 'Tuxedos & Suits' ? 'Suits and Tuxedos' : displayName}</h1>}
          />

          {this.props.activeCategory.name === 'preconfigured' && (
            <p className="mt-16 max-w-lg">Pick your look from 20+ suit and tuxedo styles in bold, daring designs and modern fits.</p>
          )}
        </div>
      </div>
    </>
  );

  toggleColorFilters(colorFilter: string) {
    this.setState((state) => {
      const colorFilters = state.colorFilters.includes(colorFilter)
        ? state.colorFilters.filter((c) => c !== colorFilter)
        : state.colorFilters.concat(colorFilter);
      return { colorFilters };
    });

    this.trackProductListFiltered();
  }

  togglePatternFilters(patternFilter: string) {
    this.setState((state) => {
      const patternFilters = state.patternFilters.includes(patternFilter)
        ? state.patternFilters.filter((p) => p !== patternFilter)
        : state.patternFilters.concat(patternFilter);
      return { patternFilters };
    });

    this.trackProductListFiltered();
  }

  toggleCategoryFilters(categoryFilter: ProductCategoryFilter) {
    this.setState((state) => {
      const categoryFilters = state.categoryFilters.includes(categoryFilter)
        ? state.categoryFilters.filter((filter) => filter.displayName !== categoryFilter.displayName)
        : state.categoryFilters.concat(categoryFilter);
      return { categoryFilters };
    });

    this.trackProductListFiltered();
  }

  toggleProcurementFilters(procurementFilter: string) {
    this.setState((state) => {
      const procurementFilters = state.procurementFilters.includes(procurementFilter)
        ? state.procurementFilters.filter((r) => r !== procurementFilter)
        : state.procurementFilters.concat(procurementFilter);
      return { procurementFilters };
    });

    this.trackProductListFiltered();
  }

  toggleTieTypesFilters(tieTypeFilter: string) {
    this.setState((state) => {
      const tieTypeFilters = state.tieTypeFilters.includes(tieTypeFilter)
        ? state.tieTypeFilters.filter((filter) => filter !== tieTypeFilter)
        : state.tieTypeFilters.concat(tieTypeFilter);

      return { tieTypeFilters };
    });

    this.trackProductListFiltered();
  }

  getFilteredProducts = () => {
    const filteredProducts = this.props.products.filter((product) => {
      // Keep products that match category filters.
      if (this.state.categoryFilters.length > 0) {
        const combinedPredicates = combinePredicatesUsingOr(this.state.categoryFilters.map((filter) => filter.value));

        const values = [product.display_name!];

        if (product.category !== 'Tie') {
          values.push(product.category!);
        }

        if (product.category === 'preconfigured' && product.type) {
          values.push(product.type);
        }

        const hasMatchingCategory = values.some(combinedPredicates);

        if (!hasMatchingCategory) {
          return false;
        }
      }

      // Keep products that match pattern filters.
      if (this.state.patternFilters.length > 0) {
        if (!this.state.patternFilters.includes(product.pattern!)) {
          return false;
        }
      }

      // Keep products that match color filters.
      // Removed special exception for brown bundles.
      if (this.state.colorFilters.length > 0) {
        if (!this.state.colorFilters.includes(product.color! as ColorFilter)) {
          return false;
        }
      }

      // Keep products that match tieType filters.
      if (this.state.tieTypeFilters.length > 0) {
        if (!this.state.tieTypeFilters.includes(product.attributeSecondary!)) {
          return false;
        }
      }

      // Keep products that match procurement filters.
      if (this.state.procurementFilters.length === 1) {
        const lowerCaseProcurementFilters = this.state.procurementFilters.map((filter) => filter.toLowerCase());

        if (lowerCaseProcurementFilters.includes('buy')) {
          return product.is_retail || product.retail_bundle?.displayable || product.type?.toLowerCase() === 'purchase';
        }

        if (lowerCaseProcurementFilters.includes('rent')) {
          return product.is_retail === false || product.type?.toLowerCase() === 'rental';
        }
      }

      return true;
    });

    return this.sortByDisplayIndex(filteredProducts);
  };

  sortByDisplayIndex = (products: Item[]) => {
    return products.sort((a, b) => {
      const aDisplayIndex = Number(a.display_index ?? 999);
      const bDisplayIndex = Number(b.display_index ?? 999);

      return aDisplayIndex - bDisplayIndex;
    });
  };

  render() {
    if (this.props.error) {
      return <Error statusCode={this.props.error} />;
    }

    const filteredProducts = this.getFilteredProducts();
    return (
      <>
        <div className="container py-32 sm:py-64">
          <div className="row">{this.headerContent(this.props.activeCategory.displayName)}</div>
          <div className="row">
            <div className="col-start-1 col-end-13 mb-64 xs:col-end-4 sm:col-end-4 md:col-end-3">
              <Filter
                activeCategory={this.props.activeCategory}
                categoryFilters={this.state.categoryFilters}
                colorFilters={this.state.colorFilters}
                patternFilters={this.state.patternFilters}
                procurementFilters={this.state.procurementFilters}
                products={this.props.products}
                tieTypeFilters={this.state.tieTypeFilters}
                toggleCategoryFilters={(category: ProductCategoryFilter) => this.toggleCategoryFilters(category)}
                toggleColorFilters={(color: string) => this.toggleColorFilters(color)}
                togglePatternFilters={(pattern: string) => this.togglePatternFilters(pattern)}
                toggleProcurementFilters={(procurementType: string) => this.toggleProcurementFilters(procurementType)}
                toggleTieTypeFilters={(tieTypeFilter: string) => this.toggleTieTypesFilters(tieTypeFilter)}
              />
            </div>

            <div className="col-start-1 col-end-13 xs:col-start-4 sm:col-start-4 md:col-start-3">
              {filteredProducts.length > 0 ? (
                <CollectionList
                  products={filteredProducts}
                  activeCategory={this.props.activeCategory}
                  activeFilter={
                    this.state.categoryFilters.length > 0 ||
                    this.state.colorFilters.length > 0 ||
                    this.state.patternFilters.length > 0 ||
                    this.state.procurementFilters.length > 0 ||
                    this.state.tieTypeFilters.length > 0
                  }
                  tuxOrSuits={this.props.activeCategory.name.toLowerCase() === 'preconfigured'}
                />
              ) : (
                <div>
                  <h3 className="text-h3">No matches</h3>
                  <p>Try selecting different filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <AboutContent category={this.props.activeCategory.slug ?? null} />
      </>
    );
  }
}

const getCollection = async (category: string) => {
  const isBundle = category.toLowerCase() === 'preconfigured';

  const response = isBundle ? await getBundles() : await getProducts(category);

  const items = await response.json();

  return items.filter((item) => {
    // Only include rental items or bundles, and retail bundles that have an
    // associated rental bundle
    const isNotRetailBundle = isBundle ? !item.is_retail || (item.displayable && !item.rental_bundle) : true;

    return isNotRetailBundle && item.media!.length > 0;
  });
};

// This function gets called at build time
export const getStaticPaths = async () => {
  const categories = productsService.categories.filter((c) => c.slug !== null);

  const paths = categories.map((c) => ({
    params: {
      category: c.slug,
    },
  }));

  // We'll pre-render only these paths at build time.
  // { fallback: false } means other routes should 404.
  return { paths, fallback: false };
};

interface Context extends BaseContext {
  params: {
    category: string;
  };
}

export const getStaticProps = withPageStaticProps(async ({ params }: Context) => {
  const { category } = params;

  const productCategory = getCategoryFromSlug(category);

  return {
    props: {
      activeCategory: {
        name: productCategory!.name,
        displayName: productCategory!.displayName,
        slug: category,
      },
      products: await getCollection(productCategory!.name),
    },
  };
});

export default withRouter(List);
